/**
 * RelatedQuestionsSection — Section 4 of QuestionEditForm (admin only).
 *
 * Self-contained: owns its expansion state, picker modal visibility,
 * picker selection, and the fetched question-text cache. Receives the
 * `relatedQuestions` array as a controlled value + `onChange` callback.
 *
 * The picker modal content is supplied by the parent via
 * `renderQuestionPicker` so admin-console can wire its own question
 * picker without this component reaching into routing or query state.
 *
 * Extracted from `QuestionEditForm.tsx` during the 2026-05-17 split.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { LatexText } from '../../latex';
import { RELATION_TYPE_OPTIONS, RELATION_TYPE_LABELS } from '../../question-set-types';
import type { RelatedQuestion } from '../formTypes';

export interface RelatedQuestionsSectionProps {
    value: RelatedQuestion[];
    onChange: (next: RelatedQuestion[]) => void;
    onGetQuestionById?: (id: string) => Promise<{ questionText: string }>;
    /** Builds the URL a row's "Open ↗" points at. A new tab, not this one: the
     *  related question is usually a catalogue question while this one may be
     *  the teacher's own draft, and navigating away mid-edit would lose the
     *  form. Omitted ⇒ no open link. */
    questionHref?: (id: string) => string;
    renderQuestionPicker?: (props: {
        selectedIds: string[];
        onSelectionChange: (ids: string[]) => void;
    }) => ReactNode;
}

export function RelatedQuestionsSection({
    value,
    onChange,
    onGetQuestionById,
    questionHref,
    renderQuestionPicker,
}: RelatedQuestionsSectionProps) {
    const [showQuestionPicker, setShowQuestionPicker] = useState(false);
    const [pickerSelectedIds, setPickerSelectedIds] = useState<string[]>([]);
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
    const [textCache, setTextCache] = useState<Record<string, string>>({});

    // Lazy-fetch question text for each related-question id so the collapsed
    // header can show a preview instead of just a UUID prefix.
    useEffect(() => {
        if (!onGetQuestionById) return;
        const ids = value
            .map(rq => rq.questionId)
            .filter(id => id && !textCache[id]);
        if (ids.length === 0) return;
        let cancelled = false;
        Promise.all(
            ids.map(id =>
                onGetQuestionById(id)
                    .then(q => ({ id, text: q.questionText }))
                    .catch(() => ({ id, text: '' }))
            )
        ).then(results => {
            if (cancelled) return;
            setTextCache(prev => {
                const next = { ...prev };
                for (const r of results) if (r.text) next[r.id] = r.text;
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [value, onGetQuestionById]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFieldChange = (
        index: number,
        field: keyof RelatedQuestion,
        next: string | Record<string, unknown> | undefined,
    ) => {
        const updated = [...value];
        updated[index] = { ...updated[index], [field]: next };
        onChange(updated);
    };

    const removeRelatedQuestion = (index: number) => {
        const updated = [...value];
        updated.splice(index, 1);
        onChange(updated);
        if (expandedIdx === index) setExpandedIdx(null);
        else if (expandedIdx !== null && expandedIdx > index) setExpandedIdx(expandedIdx - 1);
    };

    const openPicker = () => {
        setPickerSelectedIds([]);
        setShowQuestionPicker(true);
    };

    const handlePickerDone = () => {
        if (pickerSelectedIds.length > 0) {
            const existingIds = new Set(value.map(rq => rq.questionId));
            const additions: RelatedQuestion[] = pickerSelectedIds
                .filter(id => !existingIds.has(id))
                .map(id => ({ questionId: id, relationType: 'similar-concept' as const, reasonString: '' }));
            if (additions.length > 0) onChange([...value, ...additions]);
        }
        setShowQuestionPicker(false);
    };

    return (
        <div className="form-section-group">
            <div className="form-section-group-header">Related Questions</div>
            <div className="form-section-group-body">
                <div className="form-section">
                    <div className="section-header">
                        <label>Related Questions ({value.length})</label>
                        <button type="button" className="btn-add-inline" onClick={openPicker}>
                            + Add Questions
                        </button>
                    </div>

                    {value.length > 0 && (
                        <div className="rq-list">
                            {value.map((rq, idx) => {
                                const isExpanded = expandedIdx === idx;
                                return (
                                    <div key={idx} className={`rq-item ${isExpanded ? 'rq-item-expanded' : ''}`}>
                                        <div
                                            className="rq-item-header"
                                            onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                                        >
                                            <div className="rq-item-header-left">
                                                <span className="rq-item-type-badge">
                                                    {RELATION_TYPE_LABELS[rq.relationType] || rq.relationType}
                                                </span>
                                                {textCache[rq.questionId] ? (
                                                    // The row header toggles the editing body, so the stem
                                                    // must not bubble its clicks: `expandInline` turns the
                                                    // trailing "…" into its own control, and tapping it
                                                    // should reveal the rest of the stem, not open the form.
                                                    <span
                                                        className="rq-item-question-text"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <LatexText
                                                            text={textCache[rq.questionId]}
                                                            truncate={120}
                                                            expandInline
                                                            style={{ fontSize: '12px' }}
                                                        />
                                                    </span>
                                                ) : (
                                                    <span className="rq-item-id" title={rq.questionId}>
                                                        {rq.questionId ? rq.questionId.substring(0, 8) + '...' : '(no ID)'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="rq-item-header-right">
                                                {questionHref && rq.questionId && (
                                                    <a
                                                        className="rq-item-open"
                                                        href={questionHref(rq.questionId)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open this question in a new tab"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        Open ↗
                                                    </a>
                                                )}
                                                <span className="rq-expand-icon">{isExpanded ? '▲' : '▼'}</span>
                                                <button
                                                    type="button"
                                                    className="btn-remove-inline"
                                                    onClick={e => { e.stopPropagation(); removeRelatedQuestion(idx); }}
                                                >x</button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="rq-item-body">
                                                <div className="rq-field">
                                                    <label>Question ID</label>
                                                    <input
                                                        type="text"
                                                        value={rq.questionId}
                                                        onChange={e => handleFieldChange(idx, 'questionId', e.target.value)}
                                                        placeholder="Question ID (UUID)"
                                                        className="related-question-id-input"
                                                    />
                                                </div>
                                                <div className="rq-field">
                                                    <label>Relation Type</label>
                                                    <select
                                                        value={rq.relationType}
                                                        onChange={e => handleFieldChange(idx, 'relationType', e.target.value)}
                                                    >
                                                        {RELATION_TYPE_OPTIONS.map(rt => (
                                                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="rq-field">
                                                    <label>Reason</label>
                                                    <input
                                                        type="text"
                                                        value={rq.reasonString}
                                                        onChange={e => handleFieldChange(idx, 'reasonString', e.target.value)}
                                                        placeholder="Reason / description..."
                                                    />
                                                </div>
                                                <div className="rq-field">
                                                    <label>Selection Criteria (JSON)</label>
                                                    <input
                                                        type="text"
                                                        value={rq.selectionCriteria ? JSON.stringify(rq.selectionCriteria) : ''}
                                                        onChange={e => {
                                                            const val = e.target.value.trim();
                                                            if (!val) {
                                                                handleFieldChange(idx, 'selectionCriteria', undefined);
                                                            } else {
                                                                try {
                                                                    handleFieldChange(idx, 'selectionCriteria', JSON.parse(val));
                                                                } catch {
                                                                    // Keep raw text — invalid JSON until user fixes it
                                                                }
                                                            }
                                                        }}
                                                        placeholder='e.g. {"difficulty": "hard", "module": "algebra"}'
                                                        className="related-question-criteria-input"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {value.length === 0 && (
                        <p className="empty-note">No related questions. Click + Add Questions to select.</p>
                    )}
                </div>
            </div>

            {/* Question Picker Modal */}
            {showQuestionPicker && renderQuestionPicker && (
                <div className="question-edit-modal-overlay">
                    <div className="rq-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="question-edit-modal-header">
                            <h2>Select Related Questions</h2>
                            <button className="question-edit-modal-close" onClick={() => setShowQuestionPicker(false)}>&times;</button>
                        </div>
                        <div className="rq-picker-body">
                            {renderQuestionPicker({
                                selectedIds: pickerSelectedIds,
                                onSelectionChange: setPickerSelectedIds,
                            })}
                        </div>
                        <div className="rq-picker-footer">
                            <button type="button" className="btn-cancel" onClick={() => setShowQuestionPicker(false)}>Cancel</button>
                            <button
                                type="button"
                                className="btn-save"
                                onClick={handlePickerDone}
                                disabled={pickerSelectedIds.length === 0}
                            >
                                Add {pickerSelectedIds.length} Question{pickerSelectedIds.length !== 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
